/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React from "react";
import { authStore } from "../../lib/api";
import { Phone, Smartphone, Mail, Circle, PhoneCall, Copy, MessageSquare } from "lucide-react";
import { toast } from "../ui/sonner";

const STATUS = {
  "in-call": { zh: "通话中", en: "In call", cls: "bg-amber-100 text-amber-800" }, ringing: { zh: "响铃中", en: "Ringing", cls: "bg-amber-100 text-amber-800" },
  calling: { zh: "拨号中", en: "Dialing", cls: "bg-amber-100 text-amber-800" }, idle: { zh: "空闲", en: "Available", cls: "bg-emerald-100 text-emerald-800" },
  offline: { zh: "离线", en: "Offline", cls: "bg-slate-100 text-slate-500" },
};
const REGION = { la: "洛杉矶 LA", sd: "圣地亚哥 SD", norcal: "北加 NorCal", hq: "总部 HQ", cn: "中国 CN" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PhoneRow({ icon: Icon, label, value, testid, zh, onDial, onSms }) {
  const copy = async () => { try { await navigator.clipboard.writeText(value); toast.success(zh ? "已复制" : "Copied"); } catch { /* ignore */ } };
  return (
    <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-sm" data-testid={testid}>
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="text-xs text-slate-400 w-20 shrink-0">{label}</span>
      {value ? (
        <>
          <span className="font-mono text-slate-800 truncate" data-testid={`${testid}-value`}>{value}</span>
          <button type="button" onClick={copy} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-slate-700" title={zh ? "复制" : "Copy"} data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5" /></button>
          {onDial && <button type="button" onClick={onDial} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-emerald-600 hover:text-emerald-800" title={zh ? "拨打" : "Call"} data-testid={`${testid}-dial`}><PhoneCall className="h-3.5 w-3.5" /></button>}
          {onSms && <button type="button" onClick={onSms} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-sky-600 hover:text-sky-800" title={zh ? "发短信" : "SMS"} data-testid={`${testid}-sms`}><MessageSquare className="h-3.5 w-3.5" /></button>}
        </>
      ) : <span className="text-slate-300">{zh ? "未填写" : "—"}</span>}
    </div>
  );
}

/* 坐席头部：头像 / 姓名 / 分公司 / 状态 / 电话信息 */
export function AgentHeader({ a, callerId, zh }) {
  const s = STATUS[a.status] || STATUS.offline;
  const dial = (number) => window.dispatchEvent(new CustomEvent("softphone:dial", { detail: { number, name: a.name } }));
  const sms = (number) => window.dispatchEvent(new CustomEvent("softphone:sms", { detail: { number, name: a.name } }));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid="agent-header">
      <div className="flex-wrap md:flex-nowrap flex items-center gap-3 min-w-0">
        <div className="relative shrink-0">
          {a.avatarFileId
            ? <img src={`${BACKEND}/api/files/${a.avatarFileId}/download?auth=${authStore.get("ali_token") || ""}`} alt={a.name} className="h-14 w-14 rounded-full object-cover border border-slate-200" />
            : <div className="h-14 w-14 rounded-full bg-slate-800 text-white flex items-center justify-center text-xl font-bold">{(a.name || a.username).slice(0, 1).toUpperCase()}</div>}
          <Circle className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 ${a.online ? "text-emerald-500 fill-emerald-500" : "text-slate-300 fill-slate-300"}`} />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-slate-900 truncate" data-testid="agent-name" data-keep-title>{zh ? (a.nameZh || a.name) : a.name}</h1>
          <p className="text-xs text-slate-500">{REGION[a.region] || a.region || "—"} · {a.customRoleLabel || a.role} · @{a.username}</p>
          <span className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 mt-1 ${s.cls}`} data-testid="agent-status">
            {a.status === "in-call" ? <PhoneCall className="h-3 w-3" /> : <Phone className="h-3 w-3" />}{zh ? s.zh : s.en}{a.peer ? ` → ${a.peer}` : ""}
          </span>
        </div>
      </div>
      <div className="md:ml-auto grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="agent-phones">
        <PhoneRow icon={Smartphone} label={zh ? "公司手机" : "Work phone"} value={a.phone} testid="agent-phone" zh={zh} onDial={() => dial(a.phone)} onSms={() => sms(a.phone)} />
        <PhoneRow icon={Smartphone} label={zh ? "个人手机" : "Personal"} value={a.personalPhone} testid="agent-personal-phone" zh={zh} onDial={() => dial(a.personalPhone)} />
        <PhoneRow icon={Phone} label={zh ? "外呼主叫号" : "Caller ID"} value={callerId} testid="agent-caller-id" zh={zh} />
        <PhoneRow icon={Mail} label={zh ? "邮箱" : "Email"} value={a.email} testid="agent-email" zh={zh} />
      </div>
    </div>
  );
}
